import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import getNextPageParam from '@rover/react-lib/src/utils/getNextPageParam';
import {
  apiPersonDetailImagesList,
  getApiPersonDetailImagesListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-person-detail-images-list';
import {
  apiPersonDetailStayMediaList,
  getApiPersonDetailStayMediaListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-person-detail-stay-media-list';
import type { Image } from '@rover/rsdk/src/apiClient/latest/model/image';
import type { PaginatedPersonImageList } from '@rover/rsdk/src/apiClient/latest/model/paginatedPersonImageList';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import emitMetric from '@rover/utilities/emitMetric';

import { PersonalizedGalleryShown } from '../analyticsEvents';

export const GALLERY_IMAGE_PAGE_SIZE = 25;

export const GALLERY_LOAD_TIMEOUT_MS = 10_000;

const getPlatform = (): 'native' | 'web' => (process.env.JS_ENV_NATIVE ? 'native' : 'web');

/**
 * Bucketed because this is a metric tag: a raw count makes every distinct
 * gallery size its own billable timeseries. Boundaries follow the page size,
 * since a gallery stalling past its first page is the failure this metric
 * exists to catch.
 */
export const getImageCountBucket = (imageCount: number): string => {
  if (imageCount <= 25) return '1-25';
  if (imageCount <= 50) return '26-50';
  if (imageCount <= 100) return '51-100';
  return '101+';
};

/**
 * The surfaces this hook backs. Reported as a metric tag because the hook's
 * loading behavior differs per surface — the web sitter profile hydrates from an
 * SSR prefetch while the others fetch in the browser — so an untagged metric
 * reads as sitter-profile traffic when it is mostly something else.
 */
export type GallerySurface = 'sitter-profile' | 'search-card' | 'ssp-landing' | 'training-cmt-card';

export type GalleryImagesData = {
  areImagesLoading: boolean;
  galleryImages: Image[];
  imageCount: number;
  onPaginate: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  petOwnerImageIds?: Set<number>;
};

type Props = {
  opk?: string;
  /** Required so a new caller cannot silently join another surface's metrics. */
  surface: GallerySurface;
  /**
   * Opt-in: this hook also backs the search-result card gallery and the sitter
   * promotion landing page, whose loads must not land in the sitter-profile
   * personalization metric.
   */
  trackPersonalization?: boolean;
};

const useGalleryImages = ({
  opk,
  surface,
  trackPersonalization = false,
}: Props): GalleryImagesData => {
  const {
    data: sitterImagesData,
    isLoading: isLoadingInitialSitterImages,
    hasNextPage: sitterImagesHasNextPage,
    isFetchingNextPage: isFetchingNextPageOfSitterImages,
    fetchNextPage: fetchNextPageOfSitterImages,
  } = useInfiniteQuery<PaginatedPersonImageList>({
    queryKey: getApiPersonDetailImagesListQueryKey(opk),
    queryFn: ({ pageParam = 1 }) =>
      apiPersonDetailImagesList(opk!, { page: pageParam, pageSize: GALLERY_IMAGE_PAGE_SIZE }),
    getNextPageParam,
    enabled: !!opk,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const sitterImagesExhausted = sitterImagesData !== undefined && !sitterImagesHasNextPage;

  const {
    data: stayImagesData,
    isLoading: isLoadingInitialStayImages,
    hasNextPage: stayImagesHasNextPage,
    isFetchingNextPage: isFetchingNextPageOfStayImages,
    fetchNextPage: fetchNextPageOfStayImages,
  } = useInfiniteQuery({
    queryKey: getApiPersonDetailStayMediaListQueryKey(opk),
    queryFn: ({ pageParam = 1 }) => apiPersonDetailStayMediaList(opk!, { page: pageParam }),
    getNextPageParam,
    enabled: !!opk,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const sitterImages = useMemo(
    () => sitterImagesData?.pages.flatMap((p) => p.results ?? []) ?? [],
    [sitterImagesData]
  );

  const stayMedia = useMemo(
    () => stayImagesData?.pages.flatMap((p) => p.results ?? []) ?? [],
    [stayImagesData]
  );

  const stayImages = useMemo(() => stayMedia.map((m) => m.object), [stayMedia]);
  const petOwnerImageIds = useMemo(
    () =>
      new Set(
        stayMedia
          .filter((m) => m.isPetOwner)
          .map((m) => Number(m.object.id))
          .filter((id) => Number.isFinite(id))
      ),
    [stayMedia]
  );

  const galleryImages = useMemo(
    () => [...sitterImages, ...(sitterImagesExhausted ? stayImages : [])],
    [sitterImages, stayImages, sitterImagesExhausted]
  );

  const imageCount =
    (sitterImagesData?.pages[0]?.count ?? 0) + (stayImagesData?.pages[0]?.count ?? 0) + 1; // +1 accounts for the sitter's profile image prepended to the gallery

  const hasNextPage = sitterImagesHasNextPage || (stayImagesHasNextPage ?? false);
  const isFetchingNextPage = isFetchingNextPageOfSitterImages || isFetchingNextPageOfStayImages;

  const fetchNextPage = useCallback(() => {
    if (sitterImagesHasNextPage) {
      fetchNextPageOfSitterImages();
    } else if (stayImagesHasNextPage) {
      fetchNextPageOfStayImages();
    }
  }, [
    sitterImagesHasNextPage,
    fetchNextPageOfSitterImages,
    stayImagesHasNextPage,
    fetchNextPageOfStayImages,
  ]);

  const areImagesLoading = isLoadingInitialSitterImages || isLoadingInitialStayImages;

  /**
   * Reported as a tag rather than acted on: stay media only joins the gallery
   * once sitter images are exhausted, so a gallery blocked solely on stay media
   * is usually holding back nothing the viewer can see. Splitting the two lets a
   * monitor scope to the sitter-images case without changing load behavior.
   */
  const blockingQuery = isLoadingInitialSitterImages ? 'sitter-images' : 'stay-media';

  const hasEmittedTimeoutRef = useRef(false);
  const hasEmittedEmptyRef = useRef(false);
  const hasEmittedPersonalizationRef = useRef(false);
  const imageCountRef = useRef(imageCount);
  imageCountRef.current = imageCount;
  const blockingQueryRef = useRef(blockingQuery);
  blockingQueryRef.current = blockingQuery;

  useEffect(() => {
    hasEmittedTimeoutRef.current = false;
    hasEmittedEmptyRef.current = false;
    hasEmittedPersonalizationRef.current = false;
  }, [opk]);

  useEffect(() => {
    if (!opk || !areImagesLoading) return undefined;

    const timer = setTimeout(() => {
      if (hasEmittedTimeoutRef.current) return;
      hasEmittedTimeoutRef.current = true;
      emitMetric('sitter_profile_gallery_load_timeout', {
        platform: getPlatform(),
        surface,
        blocking_query: blockingQueryRef.current,
        expected_image_count_bucket: getImageCountBucket(imageCountRef.current),
      });
    }, GALLERY_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timer);
    // imageCount and the blocking query are read through refs at the deadline so
    // that updating either does not restart the timer.
  }, [opk, areImagesLoading, surface]);

  useEffect(() => {
    if (areImagesLoading || hasEmittedEmptyRef.current) return;
    if (imageCount > 1 && galleryImages.length === 0) {
      hasEmittedEmptyRef.current = true;
      emitMetric('sitter_profile_gallery_rendered_empty', { platform: getPlatform(), surface });
    }
  }, [areImagesLoading, imageCount, galleryImages.length, surface]);

  const personalization = sitterImagesData?.pages[0]?.personalization;

  useEffect(() => {
    if (!trackPersonalization || areImagesLoading || hasEmittedPersonalizationRef.current) return;
    if (!personalization) return;
    hasEmittedPersonalizationRef.current = true;
    emitAnalyticsEvent(
      new PersonalizedGalleryShown({
        personalized: personalization.personalized,
        topScore: personalization.topScore,
        topPhotoMatched: personalization.topPhotoMatched,
        topScores: personalization.topScores,
        arm: personalization.arm,
        signalSource: personalization.signalSource,
        personId: opk,
      })
    );
  }, [areImagesLoading, personalization, opk, trackPersonalization]);

  return {
    areImagesLoading,
    galleryImages,
    imageCount,
    onPaginate: fetchNextPage,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    petOwnerImageIds,
  };
};

export default useGalleryImages;
